import type { OperationStoreState, StoreAction } from './types'
import { reduce, createInitialState } from './reducer'

export type StoreListener = (state: OperationStoreState) => void

/**
 * Centralized event-sourced store for all trading operations.
 * Single source of truth for operations and market data.
 * Consumed by providers (Phase 3) and marker renderer (Phase 4).
 */
export class OperationStore {
  private state: OperationStoreState
  private listeners: Set<StoreListener> = new Set()

  constructor() {
    this.state = createInitialState()
  }

  /**
   * Get current state snapshot.
   */
  getState(): OperationStoreState {
    return this.state
  }

  /**
   * Dispatch an action to the reducer.
   * Synchronously updates state and notifies listeners if state changed.
   */
  dispatch(action: StoreAction): void {
    const prev = this.state
    this.state = reduce(prev, action)

    // Only notify if state reference changed (reducer returned new object)
    if (this.state !== prev) {
      this.notify()
    }
  }

  /**
   * Subscribe to state changes.
   * Returns unsubscribe function.
   */
  subscribe(listener: StoreListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Clean up store and all subscriptions.
   */
  destroy(): void {
    this.listeners.clear()
    this.state = createInitialState()
  }

  /**
   * Notify all listeners of state change.
   * Errors in listeners don't prevent other listeners from being called.
   */
  private notify(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.state)
      } catch (error) {
        console.error('[OperationStore] Listener error:', error)
      }
    })
  }
}
